import { UserRole } from '../services/types';

export type NotificationCategory =
  | 'warehouse'
  | 'operations'
  | 'transport'
  | 'finance'
  | 'general';

export interface NotificationCategoryConfig {
  id: NotificationCategory;
  label: string;
  description: string;
  notificationTypePrefixes: string[];
}

export const NOTIFICATION_CATEGORIES: NotificationCategoryConfig[] = [
  {
    id: 'warehouse',
    label: 'Warehouse',
    description: 'Warehouse listings, matching, and proposals',
    notificationTypePrefixes: ['Warehouse', 'WarehouseMatch'],
  },
  {
    id: 'operations',
    label: 'Operations',
    description: 'Supply chain processes, inventory, and shipments',
    notificationTypePrefixes: [
      'SupplyChainProcess',
      'InventorySku',
      'InventoryProduct',
    ],
  },
  {
    id: 'transport',
    label: 'Transport',
    description: 'Fleet and vehicle management',
    notificationTypePrefixes: ['Vehicle'],
  },
  {
    id: 'finance',
    label: 'Finance',
    description: 'Billing cycles, invoices, and payments',
    notificationTypePrefixes: ['BillingCycle'],
  },
  {
    id: 'general',
    label: 'General',
    description: 'Onboarding, checklists, and system notifications',
    notificationTypePrefixes: ['StarterChecklist'],
  },
];

export const ROLE_DEFAULT_CATEGORIES: Record<
  UserRole,
  NotificationCategory[]
> = {
  WarehouseManager: [
    'warehouse',
    'operations',
    'transport',
    'finance',
    'general',
  ],
  WarehouseClient: ['warehouse', 'operations', 'finance', 'general'],
  WarehouseOperator: ['operations', 'general'],
  BusinessUserManager: ['warehouse', 'operations', 'general'],
  FinanceAdmin: ['finance', 'general'],
};

export function getNotificationCategory(
  notificationType: string
): NotificationCategory {
  const prefix = notificationType.split('.')[0];
  for (const category of NOTIFICATION_CATEGORIES) {
    if (category.notificationTypePrefixes.includes(prefix)) {
      return category.id;
    }
  }
  return 'general';
}

export function getDefaultCategoriesForRoles(
  roles: UserRole[]
): NotificationCategory[] {
  const categorySet = new Set<NotificationCategory>();
  for (const role of roles) {
    const defaults = ROLE_DEFAULT_CATEGORIES[role];
    if (defaults) {
      defaults.forEach((c) => categorySet.add(c));
    }
  }
  categorySet.add('general');
  return Array.from(categorySet);
}

export function getAvailableCategoriesForRoles(
  roles: UserRole[]
): NotificationCategoryConfig[] {
  const availableIds = getDefaultCategoriesForRoles(roles);
  return NOTIFICATION_CATEGORIES.filter((c) => availableIds.includes(c.id));
}
